import ConicalFlask from './ConicalFlask';
import { SCENE_CLASSES } from '../../constants/classNames';
import { KID_EXPRESSIONS, MOUTH_SHAPES } from '../../constants/effects';
import { joinClassNames } from '../../utils/classNameUtils';

const SKIN = '#f6c7a1';
const HAIR = '#6b3f22';
const COAT = '#f5eedc';
const COAT_SHADE = '#e0d6bd';
const SHIRT = '#2a9d8f';
const JEANS = '#3f6fb5';
const SHOES = '#7a4a2a';
const OUTLINE = '#3b2a20';

// Curly hair is a cluster of circles: [cx, cy, r]
const HAIR_BACK_CURLS = [
  [202, 70, 17], [214, 52, 18], [232, 42, 19], [252, 42, 19], [268, 52, 18],
  [280, 70, 17], [197, 90, 12], [284, 90, 12],
];
const HAIR_FRINGE_CURLS = [
  [214, 60, 11], [229, 53, 12], [245, 51, 12], [260, 55, 11], [271, 64, 9],
];

const SMILE_PATH = 'M223 101 Q240 128 257 101 Z';
const SMILE_CLIP_ID = 'fizzy-smile-clip';

const LEFT_HAND = { x: 150, y: 104 };
const RIGHT_HAND = { x: 330, y: 104 };

function RaisedArm({ shoulderX, hand, flask, sideClassName }) {
  const controlX = (shoulderX + hand.x) / 2;
  return (
    <g className={joinClassNames(SCENE_CLASSES.hand, sideClassName)}>
      <path
        d={`M${shoulderX} 146 Q${controlX} 146 ${hand.x} ${hand.y + 10}`}
        stroke={COAT}
        strokeWidth="17"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d={`M${shoulderX} 146 Q${controlX} 146 ${hand.x} ${hand.y + 10}`}
        stroke={COAT_SHADE}
        strokeWidth="2"
        fill="none"
        transform="translate(0 8)"
      />
      <ConicalFlask x={hand.x} y={hand.y - 8} liquidColor={flask.liquidColor} label={flask.label} />
      <circle cx={hand.x} cy={hand.y + 6} r="9" fill={SKIN} stroke={OUTLINE} strokeWidth="1" />
    </g>
  );
}

/**
 * The three mouth shapes Fizzy switches between when talking.
 */
function SmileMouth({ shape }) {
  if (shape === MOUTH_SHAPES.OVAL) {
    return <ellipse cx="240" cy="108" rx="7" ry="6.5" fill="#7a2e2e" stroke={OUTLINE} strokeWidth="1.5" />;
  }
  if (shape === MOUTH_SHAPES.TEETH) {
    return (
      <>
        <path d={SMILE_PATH} fill="#7a2e2e" stroke={OUTLINE} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M225 102 H255" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
        {/* bottom teeth: clipped to the mouth so the bar sits right on the lower lip */}
        <clipPath id={SMILE_CLIP_ID}>
          <path d={SMILE_PATH} />
        </clipPath>
        <path d="M234 113.5 H246" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" clipPath={`url(#${SMILE_CLIP_ID})`} />
      </>
    );
  }
  return <path d="M226 104 Q240 117 254 104" stroke={OUTLINE} strokeWidth="3" fill="none" strokeLinecap="round" />;
}

/**
 * Bright open eyes (that blink now and then) and a smile whose shape
 * changes while Fizzy talks.
 */
function HappyFace({ mouthShape }) {
  return (
    <>
      {/* blinking swaps these two groups instead of squashing the eyes, so they stay sharp */}
      <g className={SCENE_CLASSES.eyesOpen}>
        <ellipse cx="222" cy="86" rx="7" ry="8" fill="#ffffff" stroke={OUTLINE} strokeWidth="1.5" />
        <ellipse cx="258" cy="86" rx="7" ry="8" fill="#ffffff" stroke={OUTLINE} strokeWidth="1.5" />
        <circle cx="222" cy="86" r="4" fill="#5a3a22" />
        <circle cx="258" cy="86" r="4" fill="#5a3a22" />
        <circle cx="222" cy="86" r="2" fill={OUTLINE} />
        <circle cx="258" cy="86" r="2" fill={OUTLINE} />
        <circle cx="223.3" cy="84.7" r="1.2" fill="#ffffff" />
        <circle cx="259.3" cy="84.7" r="1.2" fill="#ffffff" />
      </g>
      <g className={SCENE_CLASSES.eyesClosed}>
        <path d="M215 87 q7 4 14 0" stroke={OUTLINE} strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d="M251 87 q7 4 14 0" stroke={OUTLINE} strokeWidth="2.5" fill="none" strokeLinecap="round" />
      </g>
      <g className={SCENE_CLASSES.mouth}>
        <SmileMouth shape={mouthShape} />
      </g>
    </>
  );
}

/**
 * Wide eyes, worried eyebrows, an "O" mouth and a sweat drop, for pops and bangs.
 */
function FrightenedFace() {
  return (
    <>
      <circle cx="222" cy="85" r="8" fill="#ffffff" stroke={OUTLINE} strokeWidth="1.5" />
      <circle cx="258" cy="85" r="8" fill="#ffffff" stroke={OUTLINE} strokeWidth="1.5" />
      <circle cx="222" cy="86" r="3" fill={OUTLINE} />
      <circle cx="258" cy="86" r="3" fill={OUTLINE} />
      <path d="M212 67 q7 -2 16 -7" stroke={OUTLINE} strokeWidth="3.5" fill="none" strokeLinecap="round" />
      <path d="M268 67 q-7 -2 -16 -7" stroke={OUTLINE} strokeWidth="3.5" fill="none" strokeLinecap="round" />
      <g className={SCENE_CLASSES.mouth}>
        <ellipse cx="240" cy="110" rx="8" ry="10" fill="#7a2e2e" stroke={OUTLINE} strokeWidth="1.5" />
      </g>
      <path d="M279 62 q-6 9 0 12 q6 -3 0 -12 z" fill="#8fd3ff" stroke="#3a8fd0" strokeWidth="1" />
    </>
  );
}

// Wrap-around goggles, the same design as the "Safety first" icon, sized to sit inside
// Fizzy's face: from cheek to cheek without covering the ears, above the mouth.
const GOGGLES_LENS_PATH =
  'M207 79.7 Q207 74.5 212.9 74.5 H267.1 Q273 74.5 273 79.7 V90 Q273 96.9 265.1 96.9 H249.9 Q246.1 96.9 244.4 93.6 Q240 87.4 235.6 93.6 Q233.9 96.9 230.1 96.9 H214.9 Q207 96.9 207 90 Z';
const GOGGLES_GRADIENT_ID = 'fizzy-goggles-lens';
const GOGGLES_RIM = '#2b4a6f';

/**
 * Safety goggles, worn at all times. The lens is only lightly tinted so the eyes show through.
 */
function LabGoggles() {
  return (
    <g className={SCENE_CLASSES.goggles}>
      <defs>
        <linearGradient id={GOGGLES_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#cfe8fb" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#8fc4ef" stopOpacity="0.45" />
        </linearGradient>
      </defs>
      {/* side tabs where the strap clips on */}
      <rect x="201" y="81" width="7" height="9" rx="2.5" fill={GOGGLES_RIM} />
      <rect x="272" y="81" width="7" height="9" rx="2.5" fill={GOGGLES_RIM} />
      <path d={GOGGLES_LENS_PATH} fill={`url(#${GOGGLES_GRADIENT_ID})`} stroke={GOGGLES_RIM} strokeWidth="3" strokeLinejoin="round" />
      {/* glint of light at the outer edge, clear of the eyes */}
      <path d="M266 93 L271 80" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" opacity="0.85" />
      <path d="M212 88 L215 80" stroke="#ffffff" strokeWidth="1.4" strokeLinecap="round" opacity="0.7" />
    </g>
  );
}

/**
 * Original cartoon kid scientist: curly hair, lab goggles, lab coat over a teal
 * shirt, jeans, holding a flask up in each hand. Smiles, or looks frightened on a pop.
 */
export default function KidScientist({
  leftFlask,
  rightFlask,
  mouthShape = MOUTH_SHAPES.CLOSED,
  expression = KID_EXPRESSIONS.HAPPY,
  showArms = true,
}) {
  return (
    <g className={joinClassNames(SCENE_CLASSES.kid, expression === KID_EXPRESSIONS.FRIGHTENED && SCENE_CLASSES.kidFrightened)}>
      {/* legs and shoes (visible under the table) */}
      <rect x="222" y="226" width="16" height="62" rx="5" fill={JEANS} />
      <rect x="242" y="226" width="16" height="62" rx="5" fill={JEANS} />
      <ellipse cx="226" cy="291" rx="14" ry="7" fill={SHOES} />
      <ellipse cx="256" cy="291" rx="14" ry="7" fill={SHOES} />

      {/* body: teal shirt with the lab coat open over it */}
      <path d="M208 136 Q240 128 272 136 L278 236 H202 Z" fill={SHIRT} />
      <path d="M206 136 L234 134 L230 236 H198 Z" fill={COAT} stroke={COAT_SHADE} strokeWidth="2" />
      <path d="M274 136 L246 134 L250 236 H282 Z" fill={COAT} stroke={COAT_SHADE} strokeWidth="2" />
      <path d="M226 132 L240 150 L254 132 Z" fill={SHIRT} />
      <path d="M224 132 L236 152 L230 134 Z M256 132 L244 152 L250 134 Z" fill={COAT_SHADE} />
      <rect x="206" y="190" width="16" height="12" rx="2" fill={COAT_SHADE} />

      {/* arms holding the flasks */}
      {showArms && (
        <>
          <RaisedArm shoulderX={212} hand={LEFT_HAND} flask={leftFlask} sideClassName={SCENE_CLASSES.handLeft} />
          <RaisedArm shoulderX={268} hand={RIGHT_HAND} flask={rightFlask} sideClassName={SCENE_CLASSES.handRight} />
        </>
      )}

      {/* neck and head */}
      <rect x="232" y="118" width="16" height="16" fill={SKIN} />
      {HAIR_BACK_CURLS.map(([cx, cy, r]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} fill={HAIR} />
      ))}
      <circle cx="199" cy="92" r="8" fill={SKIN} />
      <circle cx="281" cy="92" r="8" fill={SKIN} />
      <circle cx="240" cy="86" r="40" fill={SKIN} />
      {HAIR_FRINGE_CURLS.map(([cx, cy, r]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} fill={HAIR} />
      ))}

      {expression === KID_EXPRESSIONS.FRIGHTENED ? <FrightenedFace /> : <HappyFace mouthShape={mouthShape} />}
      <LabGoggles />
    </g>
  );
}
